import { useCallback, useState } from "react";
import {
  connectMCP,
  discoverMCP,
  firstDiscoverError,
  MCPConnectError,
} from "@/services/mcp-service";
import { REQUIRES_CONNECT_CALL, type DerivedAuthType } from "@/lib/mcp-auth-derive";
import { useMcpOAuthConnect } from "@/hooks/use-mcp-oauth-connect";

/** Why a connect attempt did not reach a connected state. */
export type McpConnectFailure =
  | { kind: "error"; message: string }
  | { kind: "discovery"; error: unknown }
  | { kind: "cancel" }
  | { kind: "popupBlocked" };

export interface ConnectAndFinishOptions {
  mcpId: string;
  /** Stored or freshly-derived auth type — decides whether /connect runs. */
  authType: DerivedAuthType;
  /** Force a catalog re-discover after a successful connect. */
  discoverAfter: boolean;
  /** Require a complete discovery before reporting success. */
  verifyDiscovery?: boolean;
  /** Fully connected (and re-discovered when asked): close the flow / navigate. */
  onSuccess: () => void;
  /** Consent or required discovery failed, or the popup was blocked. */
  onFailure: (reason: McpConnectFailure) => void;
}

/**
 * Shared tail of the Add and Edit save pipelines: call executor `/v1/mcp/connect`
 * and, when it returns `needs_oauth`, launch the consent popup; on success
 * optionally force a catalog re-discover. Wraps {@link useMcpOAuthConnect} so the
 * popup state stays a single source of truth — callers keep their own
 * orchestration (persist, probe, auth-config upsert) and hand only the connect
 * step to `connectAndFinish`.
 *
 * Error semantics are preserved from the call sites this replaces:
 *  - `connectMCP` rejections PROPAGATE to the caller's try/catch (so the existing
 *    MCPConnectError → localized-message handling is unchanged).
 *  - Discovery is best-effort unless verifyDiscovery is enabled; verified
 *    failures reach onFailure, including those after an OAuth callback.
 */
export function useMcpConnect() {
  const [verifyingDiscovery, setVerifyingDiscovery] = useState(false);
  const { waitingForOauth, setWaitingForOauth, startOAuth, disposeOAuth } =
    useMcpOAuthConnect();

  const connectAndFinish = useCallback(
    async ({
      mcpId,
      authType,
      discoverAfter,
      verifyDiscovery = false,
      onSuccess,
      onFailure,
    }: ConnectAndFinishOptions): Promise<void> => {
      const finish = async () => {
        if (discoverAfter || verifyDiscovery) {
          setVerifyingDiscovery(true);
          try {
            const catalog = await discoverMCP(mcpId, true);
            const failure = verifyDiscovery ? firstDiscoverError(catalog) : null;
            if (failure) {
              throw new MCPConnectError(failure.code ?? "", failure.detail);
            }
          } catch (error) {
            if (verifyDiscovery) {
              onFailure({ kind: "discovery", error });
              return;
            }
          } finally {
            setVerifyingDiscovery(false);
          }
        }
        onSuccess();
      };

      if (REQUIRES_CONNECT_CALL.has(authType)) {
        const result = await connectMCP(mcpId);
        if (result.status === "needs_oauth") {
          const started = startOAuth(result.authorization_url, {
            state: result.state,
            onComplete: () => {
              void finish();
            },
            onError: (message) => onFailure({ kind: "error", message }),
            onCancel: () => onFailure({ kind: "cancel" }),
          });
          if (!started) onFailure({ kind: "popupBlocked" });
          return;
        }
      }

      await finish();
    },
    [startOAuth],
  );

  return {
    waitingForOauth,
    setWaitingForOauth,
    verifyingDiscovery,
    disposeOAuth,
    connectAndFinish,
  };
}
